import Vue from "vue";
import Package from "../../package.json";

export function error(err) {
  throw new Error(`[vue-bimface] ${err}`);
}

export function log(...msg) {
  console.log(
    `%cvue-bimface v${Package.version}`,
    "background:linear-gradient(120deg, #6666ff, #0099ff , #ff3399, #6666ff);background-clip:text;color:white;border-radius: 4px;padding: 1px 5px;font-weight:bold;margin-right:5px",
    ...msg
  );
}

export function createWebApp3DConfig(config) {
  let webApp3DConfig = new Glodon.Bimface.Application.WebApplication3DConfig();
  let keys = Object.keys(config);
  for (const key of keys) {
    webApp3DConfig[key] = config[key];
  }
  return webApp3DConfig;
}

export function createWebApp3D(config, viewToken = null) {
  let app3D = new Glodon.Bimface.Application.WebApplication3D(config);
  if (viewToken) {
    app3D.addView(viewToken);
  }
  return app3D;
}

export function loadBimfaceSDK(viewToken) {
  return new Promise((resolve, reject) => {
    let config = new BimfaceSDKLoaderConfig();
    config.viewToken = viewToken;
    BimfaceSDKLoader.load(
      config,
      (viewMetaData) => {
        // console.log(viewMetaData)
        resolve(viewMetaData);
      },
      (err) => {
        reject(err);
      }
    );
  });
}

export function createExtObjMng(viewer3D) {
  return new Glodon.Bimface.Plugins.ExternalObject.ExternalObjectManager(
    viewer3D
  );
}

export async function addExternalObject(
  extObjMng,
  objUrl,
  viewer3D,
  name,
  position = null,
  scale = null
) {
  return new Promise((resolve) => {
    extObjMng.loadObject({ name, url: { objectUrl: objUrl } }, function () {
      let objId = extObjMng.getObjectIdByName(name);
      position &&
        extObjMng.translate(
          objId,
          new THREE.Vector3(position.x, position.y, position.z)
        );
      scale &&
        extObjMng.scale(objId, new THREE.Vector3(scale.x, scale.y, scale.z));
      viewer3D.render();
      resolve(objId);
    });
  });
}

export function cloneExtObj(
  extObjMng,
  id,
  name,
  position = null,
  scale = null
) {
  let cloned = extObjMng.clone(id, name);
  position && extObjMng.translate(cloned, position);
  scale && extObjMng.scale(cloned, scale);
  return cloned;
}

export function registerClickInfo(viewer3D, handler = null) {
  viewer3D.addEventListener(
    Glodon.Bimface.Viewer.Viewer3DEvent.MouseClicked,
    (objData) => {
      handler && handler(objData);
    }
  );
}

export function createDrawableContainer(viewer3D) {
  let drawableConfig =
    new Glodon.Bimface.Plugins.Drawable.DrawableContainerConfig();
  drawableConfig.viewer = viewer3D;
  return new Glodon.Bimface.Plugins.Drawable.DrawableContainer(drawableConfig);
}

export function createLabel(viewer3D, drawableContainer, position, content) {
  // 构造自定义标签配置项
  let config = new Glodon.Bimface.Plugins.Drawable.CustomItemConfig();
  // 设置自定义标签的内容
  config.content = content;
  // 设置自定义标签的viewer对象
  config.viewer = viewer3D;
  // 设置自定义标签的不透明度
  config.opacity = 1;
  // 设置自定义标签的坐标
  config.worldPosition = position;
  // 构造自定义标签对象
  let customItem = new Glodon.Bimface.Plugins.Drawable.CustomItem(config);
  customItem.enableDepthTest(true);
  customItem.enableDepthTest(function (isObstructed) {
    isObstructed ? customItem.hide() : customItem.show();
  });
  // 将自定义标签添加到标签容器中
  drawableContainer.addItem(customItem);
  return customItem;
}

export function setCameraStatus(viewer3D, position, target, up) {
  // let model3D = viewer3D.getModel();
  viewer3D.setCameraStatus({
    name: "persp",
    position,
    target,
    up,
    near: 1000,
    far: 1000,
    zoom: 1,
    version: 1,
    fov: 45,
    aspect: 1,
    coordinateSystem: "world",
  });
}

export function createSplineCurve(
  viewer3D,
  points,
  color,
  width,
  lineStyle,
  type = "spline"
) {
  return new Glodon.Bimface.Plugins.Geometry.SplineCurve({
    viewer: viewer3D,
    points,
    color,
    width,
    style: lineStyle,
    type,
  });
}

export function createSolidLineType() {
  return { lineType: "Continuous", lineStyle: null };
}

export function createDashedLineType(dashLength, gapLength) {
  return {
    lineType: "Dashed",
    lineStyle: { dashLength: dashLength, gapLength: gapLength },
  };
}

export function updateSplineCurvePoints(viewer3D, curve, points) {
  curve.setPoints(points);
  viewer3D.render();
}

export function createPathAnimation(objIds, viewer3D, path, time, loop = true) {
  let config = new Glodon.Bimface.Plugins.Animation.PathAnimationConfig();
  config.viewer = viewer3D;
  config.path = path;
  config.time = time;
  config.loop = loop;
  config.objectNames = Array.isArray(objIds) ? objIds : [objIds];
  config.isPitchEnabled = true;
  config.isYawEnabled = true;
  config.originYaw = 0.5 * Math.PI;
  return new Glodon.Bimface.Plugins.Animation.PathAnimation(config);
}

export function createAnchorManager(viewer3D) {
  // 构造并配置三维锚点管理器配置项
  let anchorMngConfig = new Glodon.Bimface.Plugins.Anchor.AnchorManagerConfig();
  anchorMngConfig.viewer = viewer3D;
  return new Glodon.Bimface.Plugins.Anchor.AnchorManager(anchorMngConfig);
}

export function createPrismPoint(
  position,
  size = 35000,
  color = null,
  duration = 1500
) {
  // 构造棱锥锚点的配置项
  let prismPointConfig = new Glodon.Bimface.Plugins.Anchor.PrismPointConfig();
  // 设置棱锥锚点的悬浮位置
  prismPointConfig.position = position;
  // 设置棱锥锚点悬浮动画循环一次的时间，以毫秒为单位
  prismPointConfig.duration = duration;
  // 设置棱锥锚点的大小
  prismPointConfig.size = size;
  // 设置颜色
  color && (prismPointConfig.color = new Glodon.Web.Graphics.Color(...color));
  // 构造棱锥锚点对象，并载入至三维锚点管理器中
  return new Glodon.Bimface.Plugins.Anchor.PrismPoint(prismPointConfig);
}

export function createAndAddPrismPoint(
  anchorMng,
  position,
  size = 35000,
  color = null,
  duration = 1500
) {
  let prismPoint = createPrismPoint(position, size, color, duration);
  anchorMng.addItem(prismPoint);
  return prismPoint;
}

export function registerViewerAddedEvent(viewer3D, handler) {
  viewer3D.addEventListener(
    Glodon.Bimface.Viewer.Viewer3DEvent.ViewAdded,
    handler
  );
}

export function addContextMenu(name, handler) {
  let menu = document.querySelector(".bf-menu.bf-menu-right");
  const MenuComponent = Vue.extend({
    methods: {
      onClick: handler,
    },
    render(createElement, context) {
      return createElement("div", {
        class: {
          "bf-menu-item": true,
        },
        domProps: {
          innerHTML: name,
        },
        on: {
          click: () => {
            handler();
            menu.style.display = "none";
          },
        },
      });
    },
  });
  const component = new MenuComponent().$mount();

  menu.append(component.$el);
}

export function addModel(viewer3D, viewToken, modelId) {
  return new Promise((resolve, reject) => {
    viewer3D.loadModel({ viewToken, modelId });
    let handler = (data) => {
      let model3D = viewer3D.getModel(modelId);
      resolve({ model3D, data });
      unloadModelAddedEvent(viewer3D, handler);
    };
    registerModelAddedEvent(viewer3D, handler);
  });
}

export function unloadModelAddedEvent(viewer3D, handler) {
  viewer3D.removeEventListener(
    Glodon.Bimface.Viewer.Viewer3DEvent.ModelAdded,
    handler
  );
}

export function registerModelAddedEvent(viewer3D, handler) {
  viewer3D.addEventListener(
    Glodon.Bimface.Viewer.Viewer3DEvent.ModelAdded,
    handler
  );
}

export async function getModelPositionByBoundingBox(model3D) {
  return new Promise((resolve) => {
    model3D.getBoundingBox((data) => {
      // 获取包围盒
      let boundingBox = data.currentBoundingBox;
      // 计算中心点
      let center = getCenterPointByBoundingBox(boundingBox);
      resolve(center);
    });
  });
}

export function getCenterPointByBoundingBox(boundingBox) {
  return {
    x: (boundingBox.min.x + boundingBox.max.x) / 2,
    y: (boundingBox.min.y + boundingBox.max.y) / 2,
    z: (boundingBox.min.z + boundingBox.max.z) / 2,
  };
}

export function getModelPosition(model3D) {
  let modelTransformation = model3D.getModelTransformation();
  return {
    x: modelTransformation[12],
    y: modelTransformation[13],
    z: modelTransformation[14],
  };
}

export function zoomToModel(model3D, viewer3D) {
  // 获取模型包围盒，将摄像机视角重新定位
  model3D.getBoundingBox((data) => {
    let boundingBox = data.currentBoundingBox;
    viewer3D.getCamera().zoomToBoundingBox({
      boundingBox,
    });
  });
}

export function checkColorObject(obj) {
  if (!obj.hasOwnProperty("color")) {
    error("颜色必须要有color属性");
  }
  if (!Array.isArray(obj.color) && obj.color.length !== 4) {
    error("color必须为数组且长度为4");
  }
}
